/**
 * English how-to guides.
 *
 * Each one answers a query whose premise is usually false, and answers it
 * honestly before it does anything else. See `types.ts` for why the `honestly`
 * field is required and rendered first.
 */

import type { HowtoBundle } from "./types";

export const en: HowtoBundle = {
  "define-jawline": {
    title: "How to Define Your Jawline — What Actually Works | Fazly",
    description:
      "Jaw definition is bone plus what sits on top of it. The bone does not change in an adult; here is what does, in the order that moves the number fastest.",
    h1: "How to define your jawline",
    standfirst: "The bone does not change. Everything on top of it does.",
    honestly:
      "The gonial angle — the sharpness of the corner — is bone, and it does not change in an adult without surgery. What changes, and changes a lot, is the soft tissue over it. That is not a consolation prize: on most people the fat and fluid over the jaw account for more of how defined it looks than the angle underneath does.",
    answer:
      "Defining a jawline means changing what sits over the bone, because the bone itself is fixed after your early twenties. Three things move it, in this order of effect. Body fat is first and largest: submental and jowl fat sit directly on the jaw line, and losing body fat overall is the only way to lose it there, since fat loss is not local. Fluid retention is second and faster — sodium, alcohol and poor sleep all round the jaw within a day, which is why the same face looks sharper on a Tuesday than on a Sunday. Posture is third: forward head carriage shortens the visible neck and blurs the line where jaw meets throat. What does not work is chewing gum, jaw exercisers or tongue posture; those build or fatigue muscle and cannot move the angle.",
    steps: [
      {
        title: "Lower body fat if there is any to lose",
        body: "This is most of the effect and there is no way around it: fat loss is systemic, so you cannot target the jaw. Nothing else on this list matters as much, and nothing else on this list will show if this one is going the wrong way.",
      },
      {
        title: "Fix the things that hold fluid",
        body: "Sodium, alcohol and short sleep all puff the lower face within twenty-four hours. This is the fastest visible change available and it is also why a before-and-after taken a week apart mostly measures what you ate.",
      },
      {
        title: "Carry your head over your shoulders",
        body: "Forward head posture shortens the visible neck and softens the boundary between jaw and throat. Correcting it does not change the jaw, but it changes how much of it a viewer can see.",
      },
      {
        title: "Photograph it the same way every time",
        body: "Camera at eye level, chin level, even light from the front. A camera above eye level with the chin down flatters the jaw enough to fake months of progress, which is what most progress photos are actually showing.",
      },
    ],
    tool: "jawline",
    faq: [
      {
        q: "Does chewing gum define your jawline?",
        a: "It grows the masseter, which is a chewing muscle on the side of the jaw. That can add a small amount of width at the back of the jaw and does nothing to the gonial angle or to the fat sitting over it, so it is not what people mean when they ask this.",
      },
      {
        q: "How long does it take to see a change?",
        a: "Fluid changes are visible in a day or two. Fat loss is visible in the face after a few kilograms, which for most people is several weeks. Anything advertised as working in a week is measuring the first of those and calling it the second.",
      },
      {
        q: "Do jaw exercisers work?",
        a: "They fatigue the masseter, same as gum with more resistance. They cannot move bone, and the marketing that says otherwise is describing muscle hypertrophy in language borrowed from surgery.",
      },
    ],
  },

  "fix-negative-canthal-tilt": {
    title: "How to Fix Negative Canthal Tilt — The Honest Answer | Fazly",
    description:
      "Canthal tilt is set by where the tendons anchor to bone, so nothing non-surgical changes it. What can change is how it reads — and whether yours is real.",
    h1: "How to fix negative canthal tilt",
    standfirst:
      "First check whether yours is real. Most photographed ones are not.",
    honestly:
      "The angle cannot be changed without surgery. It is set by where the lateral canthal tendon anchors to the orbital rim, and no exercise, massage, routine or supplement moves a tendon insertion. Anything sold as doing so is not doing so. What is worth your time instead is finding out whether the tilt you measured is real, because most negative readings from phone photographs are the camera.",
    answer:
      "There is no non-surgical way to change canthal tilt, because the angle is determined by where the lateral canthal tendon attaches to the orbital rim. What can be changed is how the eye area reads and whether the measurement was real in the first place. Start there: a phone held below eye level at arm's length applies enough perspective distortion to flatten or reverse the apparent tilt, which is why a mirror and a selfie so often disagree, and why most negative readings people arrive with are the photograph. Re-measure at eye level, as far from your face as your arm allows, in flat frontal light. If the tilt is genuinely negative it is still common, appears in every population, and is a description of an angle rather than a defect. What does change the appearance is under-eye volume, brow position and sleep.",
    steps: [
      {
        title: "Re-measure at eye level",
        body: "Camera at the height of your own eyes, as far back as your arm reaches, head level, flat light from in front. This single change reverses the sign of the reading for a lot of people.",
      },
      {
        title: "Rule out puffiness",
        body: "Under-eye fluid lifts the lower lid and changes where the outer corner appears to sit. Measure in the evening as well as the morning; if the two disagree by more than a degree or two you are measuring sleep, not anatomy.",
      },
      {
        title: "Check the brow, not the eye",
        body: "A low or flat brow tail makes a neutral tilt read as negative. That is the part that responds to grooming, and it changes the impression without touching the measurement.",
      },
      {
        title: "Stop if the answer is surgical",
        body: "Canthoplasty and canthopexy are the procedures that change this angle, and they are surgery on the eyelid. That is a decision for a surgeon, not for a website, and this one has nothing further to offer on it.",
      },
    ],
    tool: "canthal-tilt",
    faq: [
      {
        q: "Can exercises fix negative canthal tilt?",
        a: "No. There is no muscle whose training moves a tendon's bony insertion, and the exercises sold for this train the orbicularis, which is not what determines the angle. Nothing in this category has evidence behind it.",
      },
      {
        q: "Why does my tilt look different in the mirror?",
        a: "Because a mirror has no lens. A phone camera at arm's length below eye level introduces perspective distortion that flattens the outer corners; the mirror shows you the angle without it, which is usually the more accurate of the two.",
      },
      {
        q: "Is negative canthal tilt actually bad?",
        a: "It is a description of an angle that appears across every population. The community treats it as a problem because a positive tilt is more common, but common is not the same as better, and no measurement here says otherwise.",
      },
    ],
  },

  "get-hunter-eyes": {
    title: "How to Get Hunter Eyes — What Is and Is Not Possible | Fazly",
    description:
      "Hunter eyes describes orbital bone and tendon position, none of which changes in an adult. What can change is the eye area around it — here is what.",
    h1: "How to get hunter eyes",
    standfirst: "Every component is bone. What surrounds it is not.",
    honestly:
      "You cannot get them. Hunter eyes describes a deep-set orbit, a positive canthal tilt and low upper-eyelid exposure, and all three are determined by the shape of the orbital bone and where the tendons anchor to it. None responds to exercise, routine or posture. This page exists because the query gets asked constantly and every other answer to it is selling something.",
    answer:
      "Hunter eyes is a community term for a combination of orbital features: an eye set deep in the socket, a positive canthal tilt, a relatively low brow, and little upper eyelid visible when the eye is open. Every one of those is skeletal or tendinous. Orbital depth is the shape of the bone around the eye; canthal tilt is where the lateral tendon anchors to the rim; brow ridge projection is bone. None of them changes in an adult without surgery, and no exercise reaches any of them. What does change is everything around them, which is more than it sounds: under-eye fluid, body fat over the orbit, sleep, brow grooming and eyebrow position all alter how deep-set and how hooded an eye reads, without touching the anatomy underneath. Measuring your canthal tilt properly is the one part of the look that returns a number, so it is where to start rather than where to finish.",
    steps: [
      {
        title: "Measure your canthal tilt properly first",
        body: "At eye level, arm's length, flat light. It is the one component of the look that is a defined number, and knowing yours tells you whether you are chasing something you already have.",
      },
      {
        title: "Reduce under-eye fluid",
        body: "Sleep, sodium and alcohol change the lower lid within a day. Puffiness fills the hollow that makes an eye read as deep-set, so this is the single largest lever on the appearance.",
      },
      {
        title: "Groom the brow, especially the tail",
        body: "The brow frames the socket and a heavy, well-defined tail is most of what reads as a low brow. It is also the only part of this list that changes in an afternoon.",
      },
      {
        title: "Lower body fat if there is any over the orbit",
        body: "Periorbital fat softens the socket's edge. Like every other fat-loss item on this site, it cannot be targeted and it is slow, and it is the last thing to try rather than the first.",
      },
    ],
    tool: "canthal-tilt",
    faq: [
      {
        q: "Can mewing give you hunter eyes?",
        a: "No. Tongue posture does not remodel adult bone, and the orbital rim is nowhere near the tongue in any case. The claim comes from generalising a mewing argument that is already unsupported for the jaw.",
      },
      {
        q: "What actually changes the eye area?",
        a: "Fluid, fat, sleep and brow grooming, in roughly that order of speed. All four change how deep-set and hooded an eye looks; none of them changes the orbit, and the difference matters if you are deciding what to spend a year on.",
      },
      {
        q: "Is there surgery for this?",
        a: "There are procedures that change canthal tilt and eyelid position. Whether any of them is a good idea is a conversation with a surgeon who has looked at your face, and it is outside what this site can usefully say.",
      },
    ],
  },

  "improve-face-symmetry": {
    title:
      "How to Improve Face Symmetry — What Moves and What Does Not | Fazly",
    description:
      "Most facial asymmetry is skeletal and permanent, and most measured asymmetry is the photograph. How to tell which one you have before doing anything.",
    h1: "How to improve face symmetry",
    standfirst: "Most of what you measured was the photo. Check that first.",
    honestly:
      "Structural asymmetry — different bone on the two sides — does not change without surgery, and it is universal: nobody is symmetric and small differences are the norm rather than a fault. But most of what people measure is not structural at all. Head rotation of a few degrees compresses one side of the face in the image, and a symmetry tool measures the image.",
    answer:
      "Improving facial symmetry starts with finding out how much of yours is real. Turning a few degrees toward the camera compresses one half of the face in the photograph, and every symmetry measurement — including this site's — measures the photograph rather than the skull. Re-shoot square-on to the lens, at eye level, with the same light falling on both sides, and a large share of measured asymmetry disappears. What remains splits in two. Skeletal asymmetry is permanent and normal; every published measurement finds it in everyone, and it is not something to fix. Habitual asymmetry does move over months: chewing predominantly on one side, sleeping on one side, and a persistent head tilt all shape soft tissue and muscle bulk unevenly. Those are worth changing. Nothing else on the usual list does anything.",
    steps: [
      {
        title: "Re-shoot square-on before concluding anything",
        body: "Camera at eye level, lens centred on your nose, both sides lit the same. Compare the two readings. If they differ by more than a few points, the first one was measuring your pose.",
      },
      {
        title: "Change the habits that are one-sided",
        body: "Chewing on one side, sleeping face-down on one side, holding a phone with one shoulder. These shape muscle and soft tissue over months, and they are the only asymmetry with an actual lever attached.",
      },
      {
        title: "Look at the neck and shoulders, not the face",
        body: "A persistent head tilt is usually postural rather than facial, and it shows up in every photograph as facial asymmetry. It is also the one on this list most likely to have a physiotherapist's answer.",
      },
      {
        title: "Accept the rest, and stop measuring weekly",
        body: "Structural asymmetry is universal and permanent. Measuring it weekly produces noise from your photography and nothing else, and the habit is worse for most people than the asymmetry.",
      },
    ],
    tool: "face-symmetry",
    faq: [
      {
        q: "Do face exercises improve symmetry?",
        a: "There is no evidence that they do, and a plausible mechanism only for the muscle-bulk component, which is small. Training one side deliberately is more likely to introduce an asymmetry than to remove one.",
      },
      {
        q: "Why is my face more asymmetric in photos than in the mirror?",
        a: "Because a mirror shows you a flipped image you have seen thousands of times and a photo shows you the unflipped one you have not. The asymmetry is identical; only your familiarity with it differs.",
      },
      {
        q: "How symmetric is a normal face?",
        a: "Every population study finds meaningful asymmetry in everyone measured. There is no threshold that separates normal from abnormal, and tools that quote a percentage — including this one — are describing a photograph, not a diagnosis.",
      },
    ],
  },

  "mew-correctly": {
    title: "How to Mew Correctly — And What It Will Not Do | Fazly",
    description:
      "The technique in full, plus the honest scope: tongue posture cannot remodel adult bone, and what it can plausibly affect is smaller than the videos claim.",
    h1: "How to mew correctly",
    standfirst:
      "The technique is simple. The claims around it are not supported.",
    honestly:
      "Tongue posture does not remodel adult facial bone. The orthodontic work the practice borrows from concerns growing children, where jaw development is genuinely still in progress, and generalising it to adults is the error the whole category rests on. What mewing can plausibly affect in an adult is muscle tone and habitual posture, which change how a jaw reads without changing the angle underneath it.",
    answer:
      "Mewing is a specific posture rather than an exercise: the whole tongue rests flat against the roof of the mouth, including the back third, the lips stay closed, the teeth touch lightly without clenching, and breathing happens through the nose. Most people who say they are mewing are holding only the tip of the tongue up, which is ordinary tongue position and not the practice. Done correctly it is a resting posture held most of the day rather than a set of repetitions. What it will not do is change your gonial angle, lift your midface or widen your palate as an adult; those claims come from paediatric orthodontics and do not transfer. What it may do is improve nasal breathing habits and resting jaw posture, both of which are worth having on their own terms.",
    steps: [
      {
        title: "Get the back third of the tongue up",
        body: "Say the letter N and hold where the tongue lands, then bring the rest of it flat to the palate behind that. If only the tip is touching, you are not doing the thing the practice describes.",
      },
      {
        title: "Close the lips, touch the teeth lightly",
        body: "Lips sealed, teeth in contact without pressure, jaw relaxed. Clenching is a different behaviour with its own problems and is not part of this.",
      },
      {
        title: "Breathe through your nose",
        body: "The posture is unmaintainable with the mouth open, so nasal breathing is a prerequisite rather than a benefit. If you cannot breathe through your nose comfortably, that is the thing to address first, and it is a medical question.",
      },
      {
        title: "Hold it at rest, not as reps",
        body: "It is a default position, not an exercise. Sets and repetitions are a misunderstanding that comes from the fitness framing, and pushing hard against the palate is how people end up with jaw pain.",
      },
    ],
    tool: "jawline",
    faq: [
      {
        q: "How long does mewing take to work?",
        a: "For the bone changes usually claimed, it does not, at any duration. For resting posture and nasal breathing habits, weeks. Any timeline offered for jaw remodelling in an adult is describing something that has not been shown to happen.",
      },
      {
        q: "Can mewing hurt you?",
        a: "Pushing hard against the palate can cause jaw and tooth discomfort, and a few case reports describe dental movement from sustained force. The correct version is a light resting posture, not pressure, which is also the version nobody demonstrates in a short video.",
      },
      {
        q: "Does mewing work for adults at all?",
        a: "Not for what it is sold for. The orthodontic literature behind it concerns children whose jaws are still growing. In adults the plausible effects are on muscle tone and habitual posture — real, but not what the before-and-after images claim.",
      },
    ],
  },
};
